import React, { useState, useEffect } from 'react'; 
import axios from 'axios';
import { Eye, EyeOff, ShieldCheck, Lock, Mail, AlertCircle, Building2, User, ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubscriptionGuard from './SubscriptionGuard'; 

const Login = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); 
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', username: '', companyName: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); 
  const [status, setStatus] = useState({ type: '', msg: '' }); 
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    if (modeParam === 'signup') {
      setMode('signup');
    } else if (modeParam === 'reset') {
      setMode('reset');
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'signup' && formData.password !== formData.confirmPassword) {
      return setStatus({ type: 'error', msg: 'The passwords do not match.' });
    }

    setStatus({ type: '', msg: '' });
    setIsLoading(true);

    try {
      let url = 'https://ai-noc.skynix.co/api/login';
      if (mode === 'signup') url = 'https://ai-noc.skynix.co/api/signup';
      if (mode === 'reset') url = 'https://ai-noc.skynix.co/api/reset-password';

      const res = await axios.post(url, formData);
      
      if (res.data.ok) {
        if (mode === 'signup') {
          const loginRes = await axios.post('https://ai-noc.skynix.co/api/login', {
            email: formData.email,
            password: formData.password
          });
          
          if (loginRes.data.ok) {
            const { username, role, tenant_id, token } = loginRes.data;
            localStorage.setItem('token', token);

            onLogin(username, role, formData.email, tenant_id);
            navigate('/pricing');
          }
        } else if (mode === 'login') {
          const { username, role, tenant_id, token } = res.data;
          localStorage.setItem('token', token);

          onLogin(username, role, formData.email, tenant_id);
          
          sessionStorage.removeItem('last_stats'); 
          navigate('/'); 
        } else if (mode === 'reset') {
          setStatus({ type: 'success', msg: 'Recovery instructions sent.' });
        }
      }
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.detail || 'Authentication failed.' });
    } finally { setIsLoading(false); }
  };


  return (
    <div className="vh-100 d-flex align-items-center justify-content-center" style={{ background: '#0b0e14', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', width: '300px', height: '300px', background: 'rgba(0, 242, 255, 0.05)', filter: 'blur(100px)', top: '10%', left: '10%', borderRadius: '50%' }}></div>

      <div style={{ width: '100%', maxWidth: '420px', padding: '20px', zIndex: 1 }}>
        <div style={{ background: '#0d1117', border: '1px solid #1e293b', borderRadius: '16px', padding: '40px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)', textAlign: 'center' }}>
          
          <div className="mb-4 d-inline-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px', background: 'rgba(0, 242, 255, 0.1)', borderRadius: '12px', color: '#00f2ff', marginBottom: '10px' }}>
            <ShieldCheck size={36} strokeWidth={1.5} />
          </div>

          <h3 className="text-white fw-bold mb-1">SKYNIX <span style={{ color: '#00f2ff' }}>AI-NOC</span></h3>
          <p className="text-secondary small mb-4">{mode === 'login' ? 'Secure SOC Authentication' : mode === 'signup' ? 'New Company Registration' : 'Account Recovery'}</p>

          {status.msg && (
            <div className={`d-flex align-items-center justify-content-center gap-2 mb-4 p-2 rounded-3 small ${status.type === 'error' ? 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-20' : 'bg-success bg-opacity-10 text-success border border-success border-opacity-20'}`}>
              {status.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />} {status.msg}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {mode === 'signup' && (
              <>
                <div className="mb-3 text-start">
                  <label className="small fw-bold text-secondary mb-1 ps-1 uppercase" style={{fontSize: '0.7rem'}}>Company Name</label>
                  <div className="position-relative">
                    <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary"><Building2 size={18} /></span>
                    <input name="companyName" type="text" className="form-control auth-input" placeholder="Acme Corp" onChange={handleChange} required />
                  </div>
                </div>
                <div className="mb-3 text-start">
                  <label className="small fw-bold text-secondary mb-1 ps-1 uppercase" style={{fontSize: '0.7rem'}}>Full Name</label>
                  <div className="position-relative">
                    <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary"><User size={18} /></span>
                    <input name="username" type="text" className="form-control auth-input" placeholder="Admin Name" onChange={handleChange} required />
                  </div>
                </div>
              </>
            )}

            <div className="mb-3 text-start">
              <label className="small fw-bold text-secondary mb-1 ps-1 uppercase" style={{fontSize: '0.7rem'}}>Email Identity</label>
              <div className="position-relative">
                <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary"><Mail size={18} /></span>
                <input name="email" type="email" className="form-control auth-input" placeholder="admin@skynix.co" onChange={handleChange} required />
              </div>
            </div>

            {mode !== 'reset' && (
              <>
                <div className="mb-3 text-start">
                  <label className="small fw-bold text-secondary mb-1 ps-1 uppercase" style={{fontSize: '0.7rem'}}>{mode === 'signup' ? 'Create Access Key' : 'Access Key'}</label>
                  <div className="position-relative">
                    <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary"><Lock size={18} /></span>
                    <input name="password" type={showPassword ? "text" : "password"} className="form-control auth-input" placeholder="••••••••" style={{paddingRight: '45px'}} onChange={handleChange} required />
                    <div className="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary pointer" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </div>
                  </div>
                </div>

                {mode === 'signup' && (
                  <div className="mb-4 text-start">
                    <label className="small fw-bold text-secondary mb-1 ps-1 uppercase" style={{fontSize: '0.7rem'}}>Confirm Access Key</label>
                    <div className="position-relative">
                      <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary"><Lock size={18} /></span>
                      <input name="confirmPassword" type={showConfirmPassword ? "text" : "password"} className="form-control auth-input" placeholder="••••••••" style={{paddingRight: '45px'}} onChange={handleChange} required />
                      <div className="position-absolute end-0 top-50 translate-middle-y me-3 text-secondary pointer" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <button className="btn btn-info w-100 fw-bold py-3 uppercase shadow-info mb-3" disabled={isLoading}>
              {isLoading ? 'Processing...' : (mode === 'login' ? 'Initialize Session' : mode === 'signup' ? 'Register Company' : 'Reset Access Key')}
            </button>
          </form>

          <div className="d-flex flex-column gap-2 mt-2 small">
            {mode === 'login' ? (
              <>
                <span className="text-info pointer" onClick={() => setMode('reset')}>Forgot key?</span>
                <span className="text-secondary">New here? <b className="text-info pointer" onClick={() => setMode('signup')}>Register</b></span>
              </>
            ) : <span className="text-info pointer" onClick={() => setMode('login')}><ArrowLeft size={14} /> Back to Login</span>}
          </div>
        </div>
      </div>
      <style>{`
        .auth-input { 
          background: #f8f9fa !important; 
          border: 1px solid #ced4da !important; 
          color: #212529 !important; 
          padding: 12px 12px 12px 45px !important; 
          border-radius: 10px !important; 
        } 
        .auth-input::placeholder {
          color: #adb5bd !important;
        }
        .auth-input:focus { 
          border-color: #00f2ff !important; 
          box-shadow: 0 0 10px rgba(0, 242, 255, 0.1) !important; 
          outline: none; 
        } 
        .pointer { cursor: pointer; } 
        .uppercase { text-transform: uppercase; } 
        .shadow-info { 
          background: #00f2ff; 
          color: #0b0e14; 
          border: none; 
          box-shadow: 0 0 15px rgba(0, 242, 255, 0.3); 
        }
      `}</style>
    </div>
  );
};

export default Login;